<template>
  <div>
    <div class="page-header bordered">
      <h1>Application Settings</h1>
    </div>

    <form>
      <h3 class="subheadline">Ticket Fee</h3>
      <div class="form-group">
        <label for="ticketFeeBaseCost" class="required">Ticket Fee Base Cost</label>
        <CurrencyInput
          v-model="applicationSettings.TICKET_FEE_BASE_COST.value"
          data-vv-as="Ticket fee base cost"
          class="form-control form-control-lg col-lg-4"
          :name="'ticketFeeBaseCost'"
          :key="'ticketFeeBaseCost'"
          v-validate="'required'"/>
        <span v-show="errors.has('ticketFeeBaseCost')" class="input-error">
          {{ errors.first('ticketFeeBaseCost') }}
        </span>
      </div>

      <div class="form-group">
        <label for="ticketFeePercentage" class="required">Ticket Fee Percentage</label>
        <b-input-group append="%" class="mb-2 mr-sm-2 mb-sm-0">
          <b-input class="form-control form-control-lg col-lg-2"
            data-vv-as="Ticket fee percentage" v-model="applicationSettings.TICKET_FEE_PERCENTAGE.value"
            name="ticketFeePercentage" v-validate="'required|min:1|min_value:0|max:5|max_value:100|decimal:2'">
          </b-input>
        </b-input-group>
        <span v-show="errors.has('ticketFeePercentage')" class="input-error">
          {{ errors.first('ticketFeePercentage') }}
        </span>
      </div>

      <div class="form-group">
        <b-row>
          <b-col>
            <label for="ticketFeePerUser">Ticket Fee Per User</label>
          </b-col>

          <b-col class="text-right">
            <b-button variant="outline-primary" size="sm" @click="showApplicationFeePerUserForm('create', null, 'ticket')">
              Add Ticket Fee for User
            </b-button>
          </b-col>
        </b-row>
        <br/>

        <b-alert v-if="Object.keys(userTicketSettings).length === 0" show>
          No ticket fee per user defined
        </b-alert>

        <table v-else role="table" class="table b-table table-striped table-hover striped">
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader" scope="col">User Email</th>
              <th role="columnheader" scope="col">Fee Base Cost</th>
              <th role="columnheader" scope="col">Fee Percentage</th>
              <th role="columnheader" scope="col" class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            <tr role="row" v-for="(userEmail, index) in Object.keys(userTicketSettings)" :key="index">
              <td role="cell"> {{ userEmail }} </td>
              <td role="cell"> {{ (userTicketSettings[userEmail][0] || {}).formatted_value }} </td>
              <td role="cell"> {{ (userTicketSettings[userEmail][1] || {}).value }} %</td>
              <td class="text-center">
                <b-button-group size="sm">
                  <b-button  variant="info" title="Edit user fee settings"
                    @click="showApplicationFeePerUserForm('edit', userEmail, 'ticket')">
                    <i class="fa fa-edit"></i>
                  </b-button>
                  <b-button variant="danger" title="Delete user fee settings"
                    @click="deleteUserFeeSetting(userEmail, 'ticket')">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </b-button-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 class="subheadline mt-5">Gift Card Fee</h3>
      <div class="form-group">
        <label for="giftCardFeeBaseCost" class="required">Gift Card Fee Base Cost</label>
        <CurrencyInput
          v-model="applicationSettings.GIFT_CARD_FEE_BASE_COST.value"
          data-vv-as="Gift Card fee base cost"
          class="form-control form-control-lg col-lg-4"
          :name="'giftCardFeeBaseCost'"
          :key="'giftCardFeeBaseCost'"
          v-validate="'required'"/>
        <span v-show="errors.has('giftCardFeeBaseCost')" class="input-error">
          {{ errors.first('giftCardFeeBaseCost') }}
        </span>
      </div>

      <div class="form-group">
        <label for="giftCardFeePercentage" class="required">Gift Card Fee Percentage</label>
        <b-input-group append="%" class="mb-2 mr-sm-2 mb-sm-0">
          <b-input class="form-control form-control-lg col-lg-2"
            data-vv-as="Gift Card fee percentage" v-model="applicationSettings.GIFT_CARD_FEE_PERCENTAGE.value"
            name="giftCardFeePercentage" v-validate="'required|min:1|min_value:0|max:5|max_value:100|decimal:2'">
          </b-input>
        </b-input-group>
        <span v-show="errors.has('giftCardFeePercentage')" class="input-error">
          {{ errors.first('giftCardFeePercentage') }}
        </span>
      </div>

      <div class="form-group">
        <b-row>
          <b-col>
            <label for="ticketFeePerUser">Gift Card Fee Per User</label>
          </b-col>

          <b-col class="text-right">
            <b-button variant="outline-primary" size="sm" @click="showApplicationFeePerUserForm('create', null, 'giftcard')">
              Add Gift Card Fee for User
            </b-button>
          </b-col>
        </b-row>
        <br/>

        <b-alert v-if="Object.keys(userGiftCardSettings).length === 0" show>
          No gift card fee per user defined
        </b-alert>

        <table v-else role="table" class="table b-table table-striped table-hover striped">
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader" scope="col">User Email</th>
              <th role="columnheader" scope="col">Fee Base Cost</th>
              <th role="columnheader" scope="col">Fee Percentage</th>
              <th role="columnheader" scope="col" class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            <tr role="row" v-for="(userEmail, index) in Object.keys(userGiftCardSettings)" :key="index">
              <td role="cell"> {{ userEmail }} </td>
              <td role="cell"> {{ (userGiftCardSettings[userEmail][0] || {}).formatted_value }} </td>
              <td role="cell"> {{ (userGiftCardSettings[userEmail][1] || {}).value }} %</td>
              <td class="text-center">
                <b-button-group size="sm">
                  <b-button  variant="info" title="Edit user fee settings"
                    @click="showApplicationFeePerUserForm('edit', userEmail, 'giftcard')">
                    <i class="fa fa-edit"></i>
                  </b-button>
                  <b-button variant="danger" title="Delete user fee settings"
                    @click="deleteUserFeeSetting(userEmail, 'giftcard')">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </b-button-group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <br/>
      <hr/>

      <div class="form-group">
        <button type="button" class="btn btn-lg btn-primary" v-on:click="validate()">
          Update Settings
        </button>
      </div>
    </form>

    <ApplicationFeePerUserForm
      :feeType="applicationFeeType"
      :userTicketSettingsList="userTicketSettings"
      :userGiftCardSettingsList="userGiftCardSettings"
      :action="userSettingAction"
      :userFeeSettings="userSettingsSelected"
      :userEmail="userEmailSelected"
      @reloadUserSettings="getUserSettings"
    />
  </div>
</template>
<script>
import ApplicationFeePerUserForm from './ApplicattionSettings/ApplicationFeePerUserForm'
const axiosConst = require('axios').default

export default {
  name: 'ApplicationSettings',
  components: {
    ApplicationFeePerUserForm
  },
  data: function () {
    return {
      applicationSettings: {
        TICKET_FEE_BASE_COST: {},
        TICKET_FEE_PERCENTAGE: {},
        GIFT_CARD_FEE_BASE_COST: {},
        GIFT_CARD_FEE_PERCENTAGE: {}
      },
      applicationFeeType: '',
      userTicketSettings: {},
      userGiftCardSettings: {},
      userSettingsSelected: [],
      userEmailSelected: '',
      userSettingAction: ''
    }
  },
  created () {
    this.getApplicationSettings()
    this.getUserSettings()
  },
  methods: {
    getApplicationSettings () {
      var self = this

      this.$http.get('/application_settings').then((response) => {
        response.data.forEach((appSetting, index) => {
          self.applicationSettings[appSetting.name] = {
            value: appSetting.value,
            id: appSetting.id
          }
        })
      })
    },
    getUserSettings () {
      var self = this
      var loader = this.$loading.show()

      this.$http.get('/user_settings').then((response) => {
        let ticketSettings = {}
        let giftCardSettings = {}

        response.data.forEach(userSetting => {
          if (userSetting.name.match(/TICKET/)) {
            ticketSettings[userSetting.user_email] = ticketSettings[userSetting.user_email] || []
            ticketSettings[userSetting.user_email].push(userSetting)
          } else {
            giftCardSettings[userSetting.user_email] = giftCardSettings[userSetting.user_email] || []
            giftCardSettings[userSetting.user_email].push(userSetting)
          }
        })

        self.userTicketSettings = ticketSettings
        self.userGiftCardSettings = giftCardSettings
      }).finally(function () {
        loader.hide()
      })
    },
    validate () {
      var self = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          self.updateSettings()
        }
      })
    },
    updateSettings () {
      var self = this
      var httpRequests = []
      var updateSttingsLoader = this.$loading.show()

      Object.keys(this.applicationSettings).forEach((appSettingKey) => {
        var appSetting = self.applicationSettings[appSettingKey]

        httpRequests.push(
          self.$http.put('application_settings/' + appSetting.id, { application_setting: { value: appSetting.value } })
        )
      })

      axiosConst.all(httpRequests).then((responses) => {
        self.$swal({
          title: 'Application settings updated!',
          type: 'success'
        })
      }).catch((error) => {
        if (error) {
          self.$swal({
            title: 'Error updating application setting',
            type: 'error'
          })
        }
      }).finally(function () {
        updateSttingsLoader.hide()
      })
    },
    showApplicationFeePerUserForm (action, userEmail, type) {
      this.userSettingAction = action
      this.userEmailSelected = userEmail
      this.userSettingsSelected = (type === 'ticket')
        ? this.userTicketSettings[userEmail]
        : this.userGiftCardSettings[userEmail]
      this.applicationFeeType = type
      this.$bvModal.show('applicationFeePerUserModal')
    },
    deleteUserFeeSetting (userEmail, type) {
      var self = this

      this.$swal({
        title: 'Confirm user fee settings deletion?',
        text: userEmail,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Confirm!'
      }).then((result) => {
        if (result.value) {
          self.deleteUserFeeSettingsLoader = self.$loading.show()
          var userSettingsToDelete = (type === 'ticket')
            ? self.userTicketSettings[userEmail]
            : self.userGiftCardSettings[userEmail]

          userSettingsToDelete.forEach(function (userSetting) {
            self.$http.delete('/user_settings/' + userSetting.id).then(function (response) {
              self.getUserSettings()

              self.$swal({
                title: 'User setting fee deleted!',
                type: 'success'
              })
            }).catch(function (error) {
              if (error) {
                self.$swal({
                  title: 'Error deleting user setting fee!',
                  type: 'error'
                })
              }
            }).finally(function () {
              self.deleteUserFeeSettingsLoader.hide()
            })
          })
        }
      })
    }
  }
}
</script>
